import {
  Badge,
  Button,
  Group,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconRefresh, IconEye } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MOCK_BRANDS_BY_NAME } from "../../mock/brands";
import { LogoImg } from "../../components/LogoImg";
import { StalenessIndicator } from "../../components/StalenessIndicator";
import type { AdapterHealth, Brand } from "../../types/brand";

function adapterHealthColor(health: AdapterHealth | null): string {
  if (health === "healthy") return "green";
  if (health === "degraded") return "yellow";
  if (health === "broken") return "red";
  return "gray";
}

function adapterHealthLabel(health: AdapterHealth | null): string {
  if (health === null) return "No adapter";
  return health.charAt(0).toUpperCase() + health.slice(1);
}

export function BrandsPage() {
  const navigate = useNavigate();
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
    };
  }, []);

  const brands = MOCK_BRANDS_BY_NAME;
  const categoryCount = useMemo(() => new Set(brands.flatMap((b) => b.categories)).size, [brands]);

  function handleRefresh(brand: Brand) {
    const existing = timersRef.current.get(brand.id);
    if (existing) clearTimeout(existing);
    setRefreshingIds((prev) => new Set(prev).add(brand.id));
    const timer = setTimeout(() => {
      setRefreshingIds((prev) => {
        const next = new Set(prev);
        next.delete(brand.id);
        return next;
      });
      timersRef.current.delete(brand.id);
      notifications.show({
        title: "Refresh complete",
        message: `${brand.name} OEM catalog refresh finished successfully.`,
        color: "green",
      });
    }, 2000);
    timersRef.current.set(brand.id, timer);
  }

  return (
    <>
      <Title order={2} mb="lg">Brands</Title>

      <Text c="dimmed" size="sm" mb="md">
        {brands.length} tracked brands across {categoryCount} categories
      </Text>

      <Table.ScrollContainer minWidth={900}>
        <Table highlightOnHover verticalSpacing="sm" fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Brand</Table.Th>
              <Table.Th>Domain</Table.Th>
              <Table.Th ta="center">Products</Table.Th>
              <Table.Th>Categories</Table.Th>
              <Table.Th>Last OEM Refresh</Table.Th>
              <Table.Th ta="center">Adapter Health</Table.Th>
              <Table.Th ta="right">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {brands.map((brand) => (
              <Table.Tr key={brand.id}>
                <Table.Td>
                  <Group gap="sm">
                    <LogoImg domain={brand.domain} alt={brand.name} size={24} />
                    <Text size="sm" fw={600}>
                      {brand.name}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {brand.domain}
                  </Text>
                </Table.Td>
                <Table.Td ta="center">
                  <Tooltip label={`${brand.trackedProductCount} tracked of ${brand.productCount} total`}>
                    <Text size="sm">
                      {brand.trackedProductCount}/{brand.productCount}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {brand.categories.map((cat) => (
                      <Badge key={cat} variant="light" color="gray" size="xs">
                        {cat}
                      </Badge>
                    ))}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <StalenessIndicator lastRefreshAt={brand.lastOemRefreshAt} />
                </Table.Td>
                <Table.Td ta="center">
                  <Badge
                    variant="light"
                    color={adapterHealthColor(brand.adapterHealth)}
                    size="xs"
                  >
                    {adapterHealthLabel(brand.adapterHealth)}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    <Tooltip label="No OEM adapter configured" disabled={brand.hasOemAdapter}>
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<IconRefresh size={14} />}
                        loading={refreshingIds.has(brand.id)}
                        disabled={!brand.hasOemAdapter}
                        onClick={() => handleRefresh(brand)}
                      >
                        Refresh
                      </Button>
                    </Tooltip>
                    <Button
                      variant={brand.pendingReviewCount > 0 ? "filled" : "subtle"}
                      color={brand.pendingReviewCount > 0 ? "blue" : "gray"}
                      size="xs"
                      leftSection={<IconEye size={14} />}
                      disabled={brand.pendingReviewCount === 0}
                      onClick={() => navigate(`/admin/brands/${brand.id}/refresh-review`)}
                    >
                      {brand.pendingReviewCount > 0 ? `Review (${brand.pendingReviewCount})` : "Review"}
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
}
